// src/lib/data-utils.ts
import {
  StorageData,
  DailyData,
  HourlyData,
  CampaignSettings,
  ConvActionData,
  CampConvData,
  PlacementData,
  QualityData,
  KeywordData,
  SearchTermData,
  ProductData,
  Campaign,
  AccountInfo,
  PMaxData,
  CampaignIdsWithSpendData,
  ChangesData,
  AcctOverviewData,
  ConvTrendsData,
  SearchTermInsightRow,
  KwCpcData,
  AssetsData,
  PMaxAssetsData,
  AdAssetsData,
  CampAssetsData,
  AgAssetsData,
  GeoPerfData,
  LPagePerfData,
  CampaignBudgetData,
  WeeklyPerformanceData,
  SmartBiddingChangesData,
  DgAdsData,
  DgAssetsData,
  DgNetworkPerfData,
} from "@/types/metrics";
import {
  OnPerformanceData,
  OnSettingsData,
  OnDisapprovedData,
  OnEmptyAdGroupsData,
} from "@/types/onboarding";
import { SHEET_TABS, SAMPLE_ACCOUNT_ID } from "@/lib/constants";
import Papa from "papaparse";
import { getDayNumber, loadSampleData } from "@/lib/sample-data-utils";
import { db } from "@/services/db";

const DEBUG = process.env.NODE_ENV === "development";

// String sanitization helper
const sanitizeName = (str: string) => {
  if (!str) return "";
  return (
    str
      // Control chars
      //.replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
      // Zero width spaces
      //.replace(/[\u200B-\u200D\uFEFF]/g, '')
      // Special chars that commonly cause issues
      //.replace(/[|<>{}[\]\/\\~`!@#$%^&*()=+]/g, '-')
      .trim()
  );
};

interface LocalBaseMetrics {
  impr: number;
  clicks: number;
  cost: number;
  conv: number;
  value: number;
  imprShare?: number;
  lostBudget?: number;
  lostRank?: number;
}

// Helper function to sanitize strings in an object
const sanitizeStrings = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "string") {
      // Fields that should be sanitized
      const shouldSanitize = [
        "campaign",
        "Campaign",
        "adGroup",
        "AdGroup",
        "keyword",
        "Keyword",
        "SearchTerm",
        "displayName",
        "Display Name",
        "placement",
        "Placement",
        "ProductTitle",
        "productTitle",
        "labels",
        "Labels",
      ].includes(key);

      result[key] = shouldSanitize ? sanitizeName(value) : value;
    } else {
      result[key] = value;
    }
  }
  return result;
};

// Create empty data structure with all required fields
function createEmptyStorageData(accountCID: string): StorageData {
  return {
    dexieId: accountCID,
    accountCID,
    timestamp: new Date().toISOString(),
    records: {},
    [SHEET_TABS.ACCOUNT]: [],
    [SHEET_TABS.DAILY]: [],
    [SHEET_TABS.HOURLY]: [],
    [SHEET_TABS.HOURLY_YEST]: [],
    [SHEET_TABS.THIRTY_DAYS]: [],
    [SHEET_TABS.P_THIRTY_DAYS]: [],
    [SHEET_TABS.SEVEN_DAYS]: [],
    [SHEET_TABS.P_SEVEN_DAYS]: [],
    [SHEET_TABS.PMAX_PERF]: [],
    [SHEET_TABS.PMAX_NETWORK]: [],
    [SHEET_TABS.DG_NETWORK_PERF]: [],
    [SHEET_TABS.DG_ADS]: [],
    [SHEET_TABS.DG_ASSETS]: [],
    [SHEET_TABS.SETTINGS]: [],
    [SHEET_TABS.PRODUCTS]: [],
    [SHEET_TABS.PLACEMENTS]: [],
    [SHEET_TABS.H_CAMP_30D]: [],
    [SHEET_TABS.H_CAMP_7D]: [],
    [SHEET_TABS.H_DOW_30D]: [],
    [SHEET_TABS.H_DOW_7D]: [],
    [SHEET_TABS.BIDDING]: [],
    [SHEET_TABS.QUALITY]: [],
    [SHEET_TABS.KEYWORD80]: [],
    [SHEET_TABS.SEARCH80]: [],
    [SHEET_TABS.CAMP_CONV]: [],
    [SHEET_TABS.CONV_ACTION]: [],
    [SHEET_TABS.KEYWORD80_PREV]: [],
    [SHEET_TABS.CAMPAIGN_IDS_SPEND]: [],
    [SHEET_TABS.CHANGES]: [],
    [SHEET_TABS.ACCT_OVERVIEW]: [],
    [SHEET_TABS.CONV_TRENDS]: [],
    [SHEET_TABS.ST_INSIGHTS]: [],
    [SHEET_TABS.KW_CPC]: [],
    [SHEET_TABS.ASSETS]: [],
    [SHEET_TABS.PMAX_ASSETS]: [],
    [SHEET_TABS.AD_ASSETS]: [],
    [SHEET_TABS.RSA_ASSETS]: [],
    [SHEET_TABS.CAMP_ASSETS]: [],
    [SHEET_TABS.AG_ASSETS]: [],
    [SHEET_TABS.GEO_PERF]: [],
    [SHEET_TABS.LPAGE_PERF]: [],
    [SHEET_TABS.CAMPAIGN_BUDGETS]: [],
    [SHEET_TABS.WEEKLY_PERFORMANCE]: [],
    [SHEET_TABS.SMART_BIDDING_CHANGES]: [],
    [SHEET_TABS.ON_PERFORMANCE]: [],
    [SHEET_TABS.ON_SETTINGS]: [],
    [SHEET_TABS.ON_DISAPPROVED]: [],
    [SHEET_TABS.ON_EMPTY_AD_GROUPS]: [],
    [SHEET_TABS.IM_PMAX_CATEGORIES]: [],
    [SHEET_TABS.IM_PMAX_SEARCH_TERMS]: [],
  };
}

async function loadCsvFile(
  tab: keyof typeof SHEET_TABS,
): Promise<Record<string, any>[]> {
  const filename = `sample_${SHEET_TABS[tab].toLowerCase()}.csv`;
  try {
    if (DEBUG) console.log(`Loading CSV file: ${filename}`);

    const response = await fetch(`/sample-data/${filename}`);
    if (!response.ok) {
      console.error(
        `HTTP error loading ${filename}:`,
        response.status,
        response.statusText,
      );
      return [];
    }
    const text = await response.text();
    if (!text.trim()) {
      console.error(`Empty or invalid CSV file: ${filename}`);
      return [];
    }

    return new Promise((resolve, reject) => {
      Papa.parse(text, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            console.warn(`Parse warnings for ${filename}:`, results.errors);
          }
          if (DEBUG) {
            console.log(`Parsed ${filename}:`, {
              rowCount: results.data.length,
              firstTwo: results.data.slice(0, 2),
            });
          }
          resolve(results.data);
        },
        error: (error) => {
          console.error(`Parse error for ${filename}:`, error);
          reject(error);
        },
      });
    });
  } catch (err) {
    console.error(`Error loading ${filename}:`, err);
    return [];
  }
}

// Transform functions moved from sample-data-utils.ts
const transformBaseMetrics = (row: Record<string, any>) => {
  // Sanitize the input row first
  const sanitizedRow = sanitizeStrings(row);

  // Get all possible variations
  const impr = Number(sanitizedRow.impr || sanitizedRow.Impressions || 0);
  const clicks = Number(sanitizedRow.clicks || sanitizedRow.Clicks || 0);
  const cost = Number(sanitizedRow.cost || sanitizedRow.Cost || 0);
  const conv = Number(sanitizedRow.conv || sanitizedRow.Conversions || 0);
  const value = Number(
    sanitizedRow.value ||
      sanitizedRow.ConvValue ||
      sanitizedRow["Conversion Value"] ||
      0,
  );
  const imprShare = Number(
    sanitizedRow.imprShare || sanitizedRow.ImprShare || 0,
  );
  const lostBudget = Number(
    sanitizedRow.lostBudget || sanitizedRow.LostToBudget || 0,
  );
  const lostRank = Number(
    sanitizedRow.lostRank || sanitizedRow.LostToRank || 0,
  );

  // Calculate derived metrics
  const CTR = impr ? (clicks / impr) * 100 : 0;
  const CvR = clicks ? (conv / clicks) * 100 : 0;
  const ROAS = cost ? value / cost : 0;
  const CPA = conv ? cost / conv : 0;
  const AOV = conv ? value / conv : 0;
  const profit = value - cost;

  return {
    impr,
    clicks,
    cost,
    conv,
    value,
    imprShare,
    lostBudget,
    lostRank,
    profit,
    CTR,
    CvR,
    ROAS,
    CPA,
    AOV,
  };
};

interface BiddingData {
  campaign: string;
  campaignId: string;
  bidStrategy: string;
  budget: number;
  status: string;
  labels: string;
  maxCpc: number;
  rtbOptIn: boolean;
  statusReasons: string;
  targetRoas: number;
  campaigns: string;
}

const transformers: Record<
  keyof typeof SHEET_TABS,
  (data: Record<string, any>[]) => any[]
> = {
  ACCOUNT: (data): AccountInfo[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        accountName: String(
          sanitizedRow.accountName || sanitizedRow.Name || "",
        ),
        accountCID: String(sanitizedRow.accountCID || sanitizedRow.CID || ""),
        currency: String(sanitizedRow.currency || sanitizedRow.Currency || "$"),
        scriptVersion: String(sanitizedRow.scriptVersion || "N/A"),
      };
    }),
  DAILY: (data): DailyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        Date: String(sanitizedRow.Date || sanitizedRow.date || ""),
        ...metrics,
      };
    }),
  HOURLY: (data): HourlyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        hour: Number(sanitizedRow.hour || sanitizedRow.Hour || 0),
        ...metrics,
      };
    }),
  HOURLY_YEST: (data): HourlyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        hour: Number(sanitizedRow.hour || sanitizedRow.Hour || 0),
        ...metrics,
      };
    }),
  THIRTY_DAYS: (data): DailyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        Date: String(sanitizedRow.Date || sanitizedRow.date || ""),
        ...metrics,
      };
    }),
  P_THIRTY_DAYS: (data): DailyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        Date: String(sanitizedRow.Date || sanitizedRow.date || ""),
        ...metrics,
      };
    }),
  SEVEN_DAYS: (data): DailyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        Date: String(sanitizedRow.Date || sanitizedRow.date || ""),
        ...metrics,
      };
    }),
  P_SEVEN_DAYS: (data): DailyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        Date: String(sanitizedRow.Date || sanitizedRow.date || ""),
        ...metrics,
      };
    }),
  SETTINGS: (data): CampaignSettings[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        status: String(sanitizedRow.status || sanitizedRow.Status || ""),
        budget: String(sanitizedRow.budget || sanitizedRow.Budget || ""),
        bidStrategy: String(
          sanitizedRow.bidStrategy || sanitizedRow.BidStrategy || "",
        ),
        bidStatus: String(
          sanitizedRow.bidStatus || sanitizedRow.BidStatus || "",
        ),
        bidType: String(sanitizedRow.bidType || sanitizedRow.BidType || ""),
        targetCpa: Number(
          sanitizedRow.targetCpa || sanitizedRow.TargetCPA || 0,
        ),
        targetRoas: Number(
          sanitizedRow.targetRoas || sanitizedRow.TargetROAS || 0,
        ),
        maxCpc: Number(sanitizedRow.maxCpc || sanitizedRow.MaxCPC || 0),
        maxCpv: Number(sanitizedRow.maxCpv || 0),
        labels: String(sanitizedRow.labels || sanitizedRow.Labels || ""),
        notes: String(sanitizedRow.notes || sanitizedRow.Notes || ""),
        channel: String(sanitizedRow.channel || sanitizedRow.Channel || ""),
        subChannel: String(
          sanitizedRow.subChannel || sanitizedRow.SubChannel || "",
        ),
        optStatus: String(
          sanitizedRow.optStatus || sanitizedRow.OptStatus || "",
        ),
        rtbOptIn:
          sanitizedRow.rtbOptIn ||
          sanitizedRow.RtbOptIn ||
          sanitizedRow.rtb_opt_in,
        statusReasons: String(sanitizedRow.statusReasons || ""),
        primaryStatus: String(sanitizedRow.primaryStatus || ""),
        servingStatus: String(sanitizedRow.servingStatus || ""),
        urlOptOut: sanitizedRow.urlOptOut,
        group: String(sanitizedRow.group || sanitizedRow.Group || ""),
      };
    }),
  PMAX_PERF: (data): PMaxData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        Date: String(sanitizedRow.Date || sanitizedRow.date || ""),
        Campaign: String(sanitizedRow.Campaign || sanitizedRow.campaign || ""),
        "Search Cost": Number(sanitizedRow["Search Cost"] || 0),
        "Display Cost": Number(sanitizedRow["Display Cost"] || 0),
        "Video Cost": Number(sanitizedRow["Video Cost"] || 0),
        "Shopping Cost": Number(sanitizedRow["Shopping Cost"] || 0),
        "Search Conv": Number(sanitizedRow["Search Conv"] || 0),
        "Display Conv": Number(sanitizedRow["Display Conv"] || 0),
        "Video Conv": Number(sanitizedRow["Video Conv"] || 0),
        "Shopping Conv": Number(sanitizedRow["Shopping Conv"] || 0),
        "Search Value": Number(sanitizedRow["Search Value"] || 0),
        "Display Value": Number(sanitizedRow["Display Value"] || 0),
        "Video Value": Number(sanitizedRow["Video Value"] || 0),
        "Shopping Value": Number(sanitizedRow["Shopping Value"] || 0),
        "Total Cost": Number(sanitizedRow["Total Cost"] || 0),
        "Total Conv": Number(sanitizedRow["Total Conv"] || 0),
        "Total Value": Number(sanitizedRow["Total Value"] || 0),
      };
    }),
  PMAX_NETWORK: (data): PMaxNetworkData[] =>
    data.map((row) => {
      const s = sanitizeStrings(row);
      return {
        Date: String(s.Date || s.date || ""),
        Campaign: String(s.Campaign || s.campaign || s.campaignName || ""),
        CampaignID: String(s.CampaignID || s.campaignId || s.campId || ""),
        TotalCost: Number(s.TotalCost || s.totalCost || 0),
        TotalConv: Number(s.TotalConv || s.totalConv || 0),
        TotalValue: Number(s.TotalValue || s.totalValue || 0),
        ShoppingCost: Number(s.ShoppingCost || s.shoppingCost || 0),
        ShoppingConv: Number(s.ShoppingConv || s.shoppingConv || 0),
        ShoppingValue: Number(s.ShoppingValue || s.shoppingValue || 0),
        SearchCost: Number(s.SearchCost || s.searchCost || 0),
        SearchConv: Number(s.SearchConv || s.searchConv || 0),
        SearchValue: Number(s.SearchValue || s.searchValue || 0),
        DisplayCost: Number(s.DisplayCost || s.displayCost || 0),
        DisplayConv: Number(s.DisplayConv || s.displayConv || 0),
        DisplayValue: Number(s.DisplayValue || s.displayValue || 0),
        YouTubeCost: Number(s.YouTubeCost || s.youtubeCost || 0),
        YouTubeConv: Number(s.YouTubeConv || s.youtubeConv || 0),
        YouTubeValue: Number(s.YouTubeValue || s.youtubeValue || 0),
        GmailCost: Number(s.GmailCost || s.gmailCost || 0),
        GmailConv: Number(s.GmailConv || s.gmailConv || 0),
        GmailValue: Number(s.GmailValue || s.gmailValue || 0),
        DiscoverCost: Number(s.DiscoverCost || s.discoverCost || 0),
        DiscoverConv: Number(s.DiscoverConv || s.discoverConv || 0),
        DiscoverValue: Number(s.DiscoverValue || s.discoverValue || 0),
        MapsCost: Number(s.MapsCost || s.mapsCost || 0),
        MapsConv: Number(s.MapsConv || s.mapsConv || 0),
        MapsValue: Number(s.MapsValue || s.mapsValue || 0),
        PartnersCost: Number(s.PartnersCost || s.partnersCost || 0),
        PartnersConv: Number(s.PartnersConv || s.partnersConv || 0),
        PartnersValue: Number(s.PartnersValue || s.partnersValue || 0),
      };
    }),
  DG_NETWORK_PERF: (data): DgNetworkPerfData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        Date: String(sanitized.Date || sanitized.date || ""),
        Campaign: String(sanitized.Campaign || sanitized.campaign || ""),
        CampaignID: String(sanitized.CampaignID || sanitized.campaignId || ""),
        TotalCost: Number(sanitized.TotalCost || sanitized.cost || 0),
        TotalConv: Number(sanitized.TotalConv || sanitized.conv || 0),
        YouTubeCost: Number(sanitized.YouTubeCost || 0),
        DiscoverCost: Number(sanitized.DiscoverCost || 0),
        GmailCost: Number(sanitized.GmailCost || 0),
        YouTubeConv: Number(sanitized.YouTubeConv || 0),
        DiscoverConv: Number(sanitized.DiscoverConv || 0),
        GmailConv: Number(sanitized.GmailConv || 0),
        YouTubeCostPercent: Number(sanitized.YouTubeCostPercent || 0),
        DiscoverCostPercent: Number(sanitized.DiscoverCostPercent || 0),
        GmailCostPercent: Number(sanitized.GmailCostPercent || 0),
        YouTubeConvPercent: Number(sanitized.YouTubeConvPercent || 0),
        DiscoverConvPercent: Number(sanitized.DiscoverConvPercent || 0),
        GmailConvPercent: Number(sanitized.GmailConvPercent || 0),
      };
    }),
  DG_ADS: (data): DgAdsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      return {
        adId: String(sanitized.adId || sanitized.AdId || ""),
        adName: String(sanitized.adName || sanitized.AdName || ""),
        adType: String(sanitized.adType || sanitized.AdType || ""),
        finalUrls: String(sanitized.finalUrls || sanitized.FinalUrls || ""),
        adStatus: String(sanitized.adStatus || sanitized.AdStatus || ""),
        adGroupId: String(
          sanitized.adGroupId ||
            sanitized.adgroupid ||
            sanitized.AdGroupId ||
            "",
        ),
        adGroupName: String(
          sanitized.adGroupName || sanitized.AdGroupName || "",
        ),
        campaignId: String(sanitized.campaignId || sanitized.CampaignId || ""),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.campaign ||
            sanitized.Campaign ||
            "",
        ),
        impr: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conv: metrics.conv,
        value: metrics.value,
        allConv: Number(sanitized.allConv || sanitized.all_conversions || 0),
        allValue: Number(
          sanitized.allValue || sanitized.all_conversion_value || 0,
        ),
      };
    }),
  DG_ASSETS: (data): DgAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        assetId: String(sanitized.assetId || sanitized.AssetId || ""),
        assetName: String(sanitized.assetName || sanitized.AssetName || ""),
        assetSource: String(
          sanitized.assetSource || sanitized.AssetSource || "",
        ),
        assetType: String(sanitized.assetType || sanitized.AssetType || ""),
        assetContent: String(
          sanitized.assetContent || sanitized.AssetContent || "",
        ),
        youtubeVideoId: String(
          sanitized.youtubeVideoId || sanitized.YoutubeVideoId || "",
        ),
        youtubeVideoTitle: String(
          sanitized.youtubeVideoTitle || sanitized.YoutubeVideoTitle || "",
        ),
        callToAction: String(
          sanitized.callToAction || sanitized.CallToAction || "",
        ),
        adId: String(sanitized.adId || sanitized.AdId || ""),
        adName: String(sanitized.adName || sanitized.AdName || ""),
        adType: String(sanitized.adType || sanitized.AdType || ""),
        adGroupName: String(
          sanitized.adGroupName || sanitized.AdGroupName || "",
        ),
        campaign: String(sanitized.campaign || sanitized.Campaign || ""),
        impr: Number(sanitized.impr || sanitized.impressions || 0),
        clicks: Number(sanitized.clicks || 0),
        cost: Number(sanitized.cost || 0),
        conv: Number(sanitized.conv || sanitized.conversions || 0),
        value: Number(sanitized.value || sanitized.conversionValue || 0),
      };
    }),
  PRODUCTS: (data): ProductData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        ProductId: String(
          sanitizedRow.ProductId || sanitizedRow.productId || "",
        ),
        ProductTitle: String(
          sanitizedRow.ProductTitle || sanitizedRow.productTitle || "",
        ),
        impressions: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conversions: metrics.conv,
        convValue: metrics.value,
        CTR: metrics.CTR,
        CvR: metrics.CvR,
        ROAS: metrics.ROAS,
        CPA: metrics.CPA,
        profit: metrics.profit,
      };
    }),
  PLACEMENTS: (data): PlacementData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        displayName: String(
          sanitizedRow.displayName || sanitizedRow["Display Name"] || "",
        ),
        placement: String(
          sanitizedRow.placement || sanitizedRow.Placement || "",
        ),
        type: String(sanitizedRow.type || sanitizedRow.Type || ""),
        targetUrl: String(
          sanitizedRow.targetUrl || sanitizedRow["Target URL"] || "",
        ),
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        impressions: Number(
          sanitizedRow.impressions || sanitizedRow.Impressions || 0,
        ),
      };
    }),
  H_CAMP_30D: (data): HourlyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        hour: Number(sanitizedRow.hour || sanitizedRow.Hour || 0),
        days: Number(sanitizedRow.days || sanitizedRow.Days || 0),
        ...metrics,
      };
    }),
  H_CAMP_7D: (data): HourlyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        hour: Number(sanitizedRow.hour || sanitizedRow.Hour || 0),
        days: Number(sanitizedRow.days || sanitizedRow.Days || 0),
        ...metrics,
      };
    }),
  H_DOW_30D: (data): HourlyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(
          sanitizedRow.campaign || sanitizedRow.Campaign || "(All Campaigns)",
        ),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "ALL",
        ),
        dow: getDayNumber(String(sanitizedRow.dow || sanitizedRow.DOW || "")),
        hour: Number(sanitizedRow.hour || sanitizedRow.Hour || 0),
        days: Number(sanitizedRow.days || sanitizedRow.Days || 0),
        ...metrics,
      };
    }),
  H_DOW_7D: (data): HourlyData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(
          sanitizedRow.campaign || sanitizedRow.Campaign || "(All Campaigns)",
        ),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "ALL",
        ),
        dow: getDayNumber(String(sanitizedRow.dow || sanitizedRow.DOW || "")),
        hour: Number(sanitizedRow.hour || sanitizedRow.Hour || 0),
        days: Number(sanitizedRow.days || sanitizedRow.Days || 0),
        ...metrics,
      };
    }),
  BIDDING: (data): BiddingData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        campaignId: String(
          sanitizedRow.campaignId || sanitizedRow.CampaignId || "",
        ),
        bidStrategy: String(
          sanitizedRow.bidStrategy || sanitizedRow.BidStrategy || "",
        ),
        budget: Number(sanitizedRow.budget || sanitizedRow.Budget || 0),
        status: String(sanitizedRow.status || sanitizedRow.Status || ""),
        labels: String(sanitizedRow.labels || sanitizedRow.Labels || ""),
        maxCpc: Number(sanitizedRow.maxCpc || sanitizedRow.MaxCpc || 0),
        rtbOptIn: Boolean(
          sanitizedRow.rtbOptIn || sanitizedRow.RtbOptIn || false,
        ),
        statusReasons: String(
          sanitizedRow.statusReasons || sanitizedRow.StatusReasons || "",
        ),
        targetRoas: Number(
          sanitizedRow.targetRoas || sanitizedRow.TargetRoas || 0,
        ),
        campaigns: String(
          sanitizedRow.campaigns || sanitizedRow.Campaigns || "",
        ),
      };
    }),
  QUALITY: (data): QualityData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        adGroup: String(
          sanitizedRow.adGroup ||
            sanitizedRow.AdGroup ||
            sanitizedRow.adGroupName ||
            "",
        ),
        keywordText: String(
          sanitizedRow.keywordText ||
            sanitizedRow.Keyword ||
            sanitizedRow.keyword ||
            "",
        ),
        matchType: String(
          sanitizedRow.matchType ||
            sanitizedRow.MatchType ||
            sanitizedRow.keywordMatchType ||
            "",
        ),
        qualityScore: Number(
          sanitizedRow.qualityScore || sanitizedRow.QualityScore || 0,
        ),
        score: Number(sanitizedRow.score || sanitizedRow.Score || 0),
        expCTR: String(sanitizedRow.expCTR || sanitizedRow.ExpCTR || ""),
        adRelevance: String(
          sanitizedRow.adRelevance || sanitizedRow.AdRelevance || "",
        ),
        landingExp: String(
          sanitizedRow.landingExp || sanitizedRow.LandingExp || "",
        ),
      };
    }),
  KEYWORD80: (data): KeywordData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        adGroup: String(
          sanitizedRow.adGroup ||
            sanitizedRow.AdGroup ||
            sanitizedRow.adGroupName ||
            "",
        ),
        keywordText: String(
          sanitizedRow.keywordText ||
            sanitizedRow.Keyword ||
            sanitizedRow.keyword ||
            "",
        ),
        keywordMatchType: String(
          sanitizedRow.keywordMatchType ||
            sanitizedRow.MatchType ||
            sanitizedRow.matchType ||
            "",
        ),
        allConv: Number(sanitizedRow.allConv || 0),
        allValue: Number(sanitizedRow.allValue || 0),
        ...metrics,
      };
    }),
  SEARCH80: (data): SearchTermData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || ""),
        searchTerm: String(sanitizedRow.searchTerm || sanitizedRow.query || ""),
        searchMatchType: String(
          sanitizedRow.matchtype || sanitizedRow.searchMatchType || "",
        ),
        allConv: Number(sanitizedRow.allConv || 0),
        allValue: Number(sanitizedRow.allValue || 0),
        date: String(sanitizedRow.date || sanitizedRow.Date || ""),
        ...metrics,
        CPC: metrics.clicks > 0 ? metrics.cost / metrics.clicks : 0,
      };
    }),
  CAMP_CONV: (data): CampConvData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        value: Number(sanitizedRow.value || sanitizedRow.Value || 0),
        conv: Number(sanitizedRow.conv || sanitizedRow.Conv || 0),
        allValue: Number(sanitizedRow.allValue || sanitizedRow.AllValue || 0),
        allConv: Number(sanitizedRow.allConv || sanitizedRow.AllConv || 0),
        networkType: String(
          sanitizedRow.networkType || sanitizedRow.NetworkType || "",
        ),
        convCategory: String(
          sanitizedRow.convCategory || sanitizedRow.ConvCategory || "",
        ),
        date: String(sanitizedRow.date || sanitizedRow.Date || ""),
      };
    }),
  CONV_ACTION: (data): ConvActionData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        date: String(sanitized.date || sanitized.Date || ""),
        convName: String(
          sanitized.convName ||
            sanitized.conversionActionName ||
            sanitized.Conversionaction ||
            sanitized.conv_name ||
            "",
        ),
        convId: String(sanitized.convId || ""),
        status: String(
          sanitized.status || sanitized.convStatus || sanitized.Status || "",
        ),
        type: String(
          sanitized.type || sanitized.convType || sanitized.Type || "",
        ),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
        countingType: String(sanitized.countingType || ""),
        includeInMetric: Boolean(sanitized.includeInMetric || false),
        primaryGoal: Boolean(sanitized.primaryGoal || false),
      };
    }),
  KEYWORD80_PREV: (data): KeywordData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        campaign: String(sanitizedRow.campaign || sanitizedRow.Campaign || ""),
        adGroup: String(
          sanitizedRow.adGroup ||
            sanitizedRow.AdGroup ||
            sanitizedRow.adGroupName ||
            "",
        ),
        keywordText: String(
          sanitizedRow.keywordText ||
            sanitizedRow.Keyword ||
            sanitizedRow.keyword ||
            "",
        ),
        keywordMatchType: String(
          sanitizedRow.keywordMatchType ||
            sanitizedRow.MatchType ||
            sanitizedRow.matchType ||
            "",
        ),
        allConv: Number(sanitizedRow.allConv || 0),
        allValue: Number(sanitizedRow.allValue || 0),
        ...metrics,
      };
    }),
  CAMPAIGN_IDS_SPEND: (data): CampaignIdsWithSpendData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        campaignId: String(sanitizedRow.campaignId || ""),
        cost: Number(sanitizedRow.cost || 0),
      };
    }),
  CHANGES: (data): ChangesData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        changeDateTime: String(
          sanitized.changeDateTime ||
            sanitized.date ||
            sanitized.changeDate ||
            "",
        ),
        changeType: String(sanitized.changeType || sanitized.type || ""),
        changedFields: String(
          sanitized.changedFields || sanitized.fields || "",
        ),
        userEmail: String(sanitized.userEmail || sanitized.user || ""),
        campaignName: String(
          sanitized.campaignName || sanitized.campaign || "",
        ),
        campaignId: String(sanitized.campaignId || sanitized.campId || ""),
        changeResourceName: String(
          sanitized.changeResourceName ||
            sanitized.resource ||
            sanitized.changeResource ||
            "",
        ),
        operation: String(sanitized.operation || sanitized.op || ""),
        biddingStrategy: String(
          sanitized.biddingStrategy || sanitized.currentBiddingStrategy || "",
        ),
        targetROAS: String(
          sanitized.targetROAS ||
            sanitized.currentTargetROASDirect ||
            sanitized.troas ||
            "",
        ),
        targetCPA: String(
          sanitized.targetCPA ||
            sanitized.currentTargetCPAMicros ||
            sanitized.tcpa ||
            "",
        ),
        directTargetROAS: String(sanitized.directTargetROAS || ""),
        channelType: String(sanitized.channelType || sanitized.channel || ""),
        channelSubType: String(
          sanitized.channelSubType || sanitized.subChannel || "",
        ),
        servingStatus: String(sanitized.servingStatus || ""),
        primaryStatus: String(sanitized.primaryStatus || ""),
        primaryReasons: String(
          sanitized.primaryReasons || sanitized.statusReasons || "",
        ),
        status: String(sanitized.status || ""),
        tcpaCeiling: String(sanitized.tcpaCeiling || ""),
        tcpaFloor: String(sanitized.tcpaFloor || ""),
        tcpaTarget: String(sanitized.tcpaTarget || ""),
        troasCeiling: String(sanitized.troasCeiling || ""),
        troasFloor: String(sanitized.troasFloor || ""),
        troasTarget: String(sanitized.troasTarget || ""),
        tsCeiling: String(sanitized.tsCeiling || ""),
        tsTarget: String(sanitized.tsTarget || ""),
        budget: String(sanitized.budget || ""),
        targetSearchNetwork: String(sanitized.targetSearchNetwork || ""),
        targetPartnerSearchNetwork: String(
          sanitized.targetPartnerSearchNetwork || "",
        ),
        targetGoogleTvNetwork: String(sanitized.targetGoogleTvNetwork || ""),
        targetGoogleSearch: String(sanitized.targetGoogleSearch || ""),
        targetContentNetwork: String(sanitized.targetContentNetwork || ""),
        targetYoutube: String(sanitized.targetYoutube || ""),
        optimizationGoalTypes: String(sanitized.optimizationGoalTypes || ""),
      };
    }),
  ACCT_OVERVIEW: (data): AcctOverviewData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        date: String(sanitizedRow.date || sanitizedRow.Date || ""),
        impressions: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conversions: metrics.conv,
        conversionValue: metrics.value,
        allConversions: Number(sanitizedRow.all_conversions || 0),
        allConversionValue: Number(sanitizedRow.all_conversion_value || 0),
        ctr: metrics.CTR,
        cpc: metrics.impr > 0 ? metrics.cost / metrics.clicks : 0,
        convRate: metrics.CvR,
        roas: metrics.ROAS,
      };
    }),
  CONV_TRENDS: (data): ConvTrendsData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        date: String(sanitizedRow.date || sanitizedRow.Date || ""),
        campaignName: String(
          sanitizedRow.campaignName ||
            sanitizedRow["Campaign Name"] ||
            sanitizedRow.campaign ||
            sanitizedRow["campaign.name"] ||
            sanitizedRow.CampaignName ||
            sanitizedRow.Campaign ||
            "",
        ),
        campaignId: String(
          sanitizedRow.campaignId ||
            sanitizedRow["Campaign ID"] ||
            sanitizedRow["campaign.id"] ||
            sanitizedRow.campId ||
            sanitizedRow.CampaignId ||
            "",
        ),
        conversionActionName: String(
          sanitizedRow.conversionActionName ||
            sanitizedRow["Conversion Action"] ||
            sanitizedRow.conversionAction ||
            sanitizedRow.convActionName ||
            sanitizedRow.ConversionActionName ||
            "",
        ),
        conversionCategory: String(
          sanitizedRow.conversionCategory ||
            sanitizedRow.Category ||
            sanitizedRow.conversionActionCategory ||
            sanitizedRow.convActionCat ||
            sanitizedRow.ConversionCategory ||
            "",
        ),
        conversions: Number(
          sanitizedRow.conversions || sanitizedRow.Conversions || 0,
        ),
        conversionValue: Number(
          sanitizedRow.conversionValue ||
            sanitizedRow["Conv. Value"] ||
            sanitizedRow.ConversionValue ||
            sanitizedRow.value ||
            0,
        ),
        allConversions: Number(
          sanitizedRow.allConversions ||
            sanitizedRow["All Conversions"] ||
            sanitizedRow.AllConversions ||
            sanitizedRow.allConv ||
            0,
        ),
        allConversionValue: Number(
          sanitizedRow.allConversionValue ||
            sanitizedRow["All Conv. Value"] ||
            sanitizedRow.AllConversionValue ||
            sanitizedRow.allValue ||
            0,
        ),
      };
    }),
  ST_INSIGHTS: (data): SearchTermInsightRow[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        insightType: String(
          sanitizedRow.insightType || sanitizedRow["Insight Type"] || "",
        ),
        searchTerm: String(
          sanitizedRow.searchTerm || sanitizedRow["Search Term"] || "",
        ),
        impressionTrend: String(
          sanitizedRow.impressionTrend ||
            sanitizedRow["Impression Trend"] ||
            "",
        ),
        currentImprPerDay: String(
          sanitizedRow.currentImprPerDay ||
            sanitizedRow["Current Impr/Day"] ||
            "",
        ),
        currentClicksPerDay: String(
          sanitizedRow.currentClicksPerDay ||
            sanitizedRow["Current Clicks/Day"] ||
            "",
        ),
        currentCostPerDay: String(
          sanitizedRow.currentCostPerDay ||
            sanitizedRow["Current Cost/Day"] ||
            "",
        ),
        currentConvPerDay: String(
          sanitizedRow.currentConvPerDay ||
            sanitizedRow["Current Conv/Day"] ||
            "",
        ),
        previousImprPerDay: String(
          sanitizedRow.previousImprPerDay ||
            sanitizedRow["Previous Impr/Day"] ||
            "",
        ),
        previousConvPerDay: String(
          sanitizedRow.previousConvPerDay ||
            sanitizedRow["Previous Conv/Day"] ||
            "",
        ),
        firstAppearance: String(
          sanitizedRow.firstAppearance ||
            sanitizedRow.FirstAppearance ||
            sanitizedRow["First Appearance"] ||
            "",
        ),
        lastAppearance: String(
          sanitizedRow.lastAppearance || sanitizedRow["Last Appearance"] || "",
        ),
        coverage: String(sanitizedRow.coverage || sanitizedRow.Coverage || ""),
        gapCount: String(
          sanitizedRow.gapCount || sanitizedRow["Gap Count"] || "",
        ),
        avgImprOnActive: String(
          sanitizedRow.avgImprOnActive ||
            sanitizedRow["Avg Impr On Active"] ||
            "",
        ),
        recommendation: String(
          sanitizedRow.recommendation || sanitizedRow.Recommendation || "",
        ),
      };
    }),
  KW_CPC: (data): KwCpcData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        campaignName: String(
          sanitizedRow.campaignName || sanitizedRow.Campaign || "",
        ),
        adGroupName: String(
          sanitizedRow.adGroupName ||
            sanitizedRow.AdGroup ||
            sanitizedRow["Ad Group"] ||
            "",
        ),
        keywordText: String(
          sanitizedRow.keywordText || sanitizedRow.Keyword || "",
        ),
        keywordMatchType: String(
          sanitizedRow.keywordMatchType ||
            sanitizedRow.MatchType ||
            sanitizedRow["Match Type"] ||
            "",
        ),
        impr: Number(sanitizedRow.impr || sanitizedRow.Impressions || 0),
        clicks: Number(sanitizedRow.clicks || sanitizedRow.Clicks || 0),
        cost: Number(sanitizedRow.cost || sanitizedRow.Cost || 0),
        avgCpc: Number(
          sanitizedRow.avgCpc ||
            sanitizedRow["Avg. CPC"] ||
            sanitizedRow.AvgCpc ||
            0,
        ),
      };
    }),
  ASSETS: (data): AssetsData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      return {
        assetId: String(sanitizedRow.assetId || ""),
        assetName: String(sanitizedRow.assetName || ""),
        assetType: String(sanitizedRow.assetType || ""),
        assetText: String(sanitizedRow.assetText || ""),
        assetImageUrl: String(sanitizedRow.assetImageUrl || ""),
        assetYoutubeVideoId: String(sanitizedRow.assetYoutubeVideoId || ""),
        assetYoutubeVideoTitle: String(
          sanitizedRow.assetYoutubeVideoTitle || "",
        ),
      };
    }),
  PMAX_ASSETS: (data): PMaxAssetsData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        date: String(sanitizedRow.date || ""),
        campaignId: String(sanitizedRow.campaignId || ""),
        campaignName: String(sanitizedRow.campaignName || ""),
        assetId: String(sanitizedRow.assetId || ""),
        assetFieldType: String(sanitizedRow.assetFieldType || ""),
        assetSource: String(sanitizedRow.assetSource || ""),
        assetGroupName: String(sanitizedRow.assetGroupName || ""),
        assetGroupId: String(sanitizedRow.assetGroupId || ""),
        impressions: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conversions: metrics.conv,
        conversionValue: metrics.value,
        videoViews: Number(sanitizedRow.videoViews || 0),
        ctr: metrics.CTR,
        cpc: metrics.impr > 0 ? metrics.cost / metrics.clicks : 0,
        cpa: metrics.CPA,
        roas: metrics.ROAS,
        cvr: metrics.CvR,
      };
    }),
  AD_ASSETS: (data): AdAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      return {
        segDate: String(
          sanitized.segDate || sanitized.seg_date || sanitized.date || "",
        ),
        campId: String(
          sanitized.campId || sanitized.camp_id || sanitized.campaignId || "",
        ),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.campaign_name ||
            sanitized.campaign ||
            "",
        ),
        campType: String(
          sanitized.campType ||
            sanitized.campaignType ||
            sanitized.camp_type ||
            sanitized.channel ||
            "",
        ),
        adGroupId: String(
          sanitized.adGroupId ||
            sanitized.ad_group_id ||
            sanitized.adgroupid ||
            "",
        ),
        adGroupName: String(sanitized.adGroupName || ""),
        adId: String(sanitized.adId || ""),
        assetId: String(sanitized.assetId || ""),
        assetFieldType: String(
          sanitized.assetFieldType ||
            sanitized.AssetFieldType ||
            sanitized.assetType ||
            sanitized.AssetType ||
            "UNKNOWN",
        ),
        pinnedField: String(
          sanitized.pinnedField || sanitized.PinnedField || "",
        ),
        assetSource: String(
          sanitized.assetSource || sanitized.asset_source || "",
        ),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
        ...metrics,
      };
    }),
  RSA_ASSETS: (data): AdAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      return {
        segDate: String(
          sanitized.segDate || sanitized.seg_date || sanitized.date || "",
        ),
        campId: String(
          sanitized.campId || sanitized.camp_id || sanitized.campaignId || "",
        ),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.campaign_name ||
            sanitized.campaign ||
            "",
        ),
        campType: String(
          sanitized.campType ||
            sanitized.campaignType ||
            sanitized.camp_type ||
            sanitized.channel ||
            "SEARCH",
        ),
        adGroupId: String(
          sanitized.adGroupId ||
            sanitized.ad_group_id ||
            sanitized.adgroupid ||
            "",
        ),
        adGroupName: String(sanitized.adGroupName || ""),
        adId: String(sanitized.adId || ""),
        assetId: String(sanitized.assetId || ""),
        assetFieldType: String(
          sanitized.assetFieldType ||
            sanitized.AssetFieldType ||
            sanitized.assetType ||
            sanitized.AssetType ||
            "UNKNOWN",
        ),
        pinnedField: String(
          sanitized.pinnedField || sanitized.PinnedField || "",
        ),
        assetSource: String(
          sanitized.assetSource || sanitized.asset_source || "",
        ),
        assetText: String(sanitized.assetText || sanitized.asset_text || ""),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
        ...metrics,
      };
    }),
  CAMP_ASSETS: (data): CampAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      return {
        segDate: String(
          sanitized.segDate || sanitized.date || sanitized.Date || "",
        ),
        campId: String(
          sanitized.campId ||
            sanitized.campaignId ||
            sanitized.camp_id ||
            sanitized.CampaignId ||
            "",
        ),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.campaign ||
            sanitized.Campaign ||
            "",
        ),
        campType: String(
          sanitized.campType ||
            sanitized.channel ||
            sanitized.campaignType ||
            sanitized.Channel ||
            "",
        ),
        assetId: String(sanitized.assetId || sanitized.AssetId || ""),
        assetFieldType: String(
          sanitized.assetFieldType ||
            sanitized.AssetFieldType ||
            sanitized.assetType ||
            sanitized.AssetType ||
            "UNKNOWN",
        ),
        assetSource: String(
          sanitized.assetSource || sanitized.AssetSource || "",
        ),
        assetStatus: String(
          sanitized.assetStatus || sanitized.status || sanitized.Status || "",
        ),
        campaignStatus: String(
          sanitized.campaignStatus || sanitized.CampaignStatus || "",
        ),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
        ...metrics,
      };
    }),
  AG_ASSETS: (data): AgAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      return {
        segDate: String(sanitized.segDate || sanitized.date || ""),
        campId: String(
          sanitized.campId || sanitized.camp_id || sanitized.campaignId || "",
        ),
        campaignName: String(
          sanitized.campaignName || sanitized.campaign || "",
        ),
        campaignStatus: String(
          sanitized.campaignStatus || sanitized.status || "",
        ),
        campType: String(sanitized.campType || sanitized.channel || ""),
        adGroupId: String(
          sanitized.adGroupId ||
            sanitized.adgroupid ||
            sanitized.ad_group_id ||
            "",
        ),
        adGroupName: String(sanitized.adGroupName || ""),
        adGroupStatus: String(
          sanitized.adGroupStatus ||
            sanitized.adGroupStatus ||
            sanitized.adgroupstatus ||
            "",
        ),
        adId: String(sanitized.adId || ""),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
        ...metrics,
      };
    }),
  GEO_PERF: (data): GeoPerfData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      return {
        campaignName: String(
          sanitized.campaignName || sanitized.Campaign || "",
        ),
        countryName: String(
          sanitized.countryName ||
            sanitized.Country ||
            sanitized.CountryName ||
            "",
        ),
        locationType: String(
          sanitized.locationType ||
            sanitized.LocationType ||
            sanitized["Location Type"] ||
            "",
        ),
        resolvedLocation: String(
          sanitized.resolvedLocation ||
            sanitized.ResolvedLocation ||
            sanitized.Region ||
            sanitized.region ||
            sanitized.State ||
            sanitized.state ||
            sanitized.County ||
            sanitized.county ||
            sanitized.Province ||
            sanitized.province ||
            sanitized.Canton ||
            sanitized.canton ||
            "",
        ),
        ...metrics,
      };
    }),
  LPAGE_PERF: (data): LPagePerfData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      return {
        segDate: String(
          sanitized.segDate || sanitized.Date || sanitized.date || "",
        ),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.Campaign ||
            sanitized.campaign ||
            "",
        ),
        adGroupName: String(
          sanitized.adGroupName || sanitized.AdGroupName || "",
        ),
        finalUrl: String(sanitized.finalUrl || sanitized.FinalUrl || ""),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
        ...metrics,
      };
    }),
  CAMPAIGN_BUDGETS: (data): CampaignBudgetData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaignName: String(
          sanitized.campaignName ||
            sanitized["Campaign Name"] ||
            sanitized.campaign ||
            sanitized.Campaign ||
            "",
        ),
        campaignId: String(
          sanitized.campaignId || sanitized["Campaign ID"] || "",
        ),
        budgetId: String(sanitized.budgetId || sanitized["Budget ID"] || ""),
        budgetName: String(
          sanitized.budgetName || sanitized["Budget Name"] || "",
        ),
        budgetAmount: Number(
          sanitized.budgetAmount || sanitized["Budget Amount"] || 0,
        ),
        deliveryMethod: String(
          sanitized.deliveryMethod || sanitized["Delivery Method"] || "",
        ),
        sharedBudget: Boolean(
          sanitized.sharedBudget === true ||
            sanitized.sharedBudget === "true" ||
            sanitized["Shared Budget"] === "TRUE",
        ),
        budgetStatus: String(
          sanitized.budgetStatus || sanitized["Budget Status"] || "",
        ),
      };
    }),
  WEEKLY_PERFORMANCE: (data): WeeklyPerformanceData[] =>
    data.map((row) => {
      const sanitizedRow = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitizedRow);
      return {
        week: String(sanitizedRow.week || sanitizedRow.Week || ""),
        campaignName: String(
          sanitizedRow.campaignName ||
            sanitizedRow.CampaignName ||
            sanitizedRow["Campaign Name"] ||
            sanitizedRow.campaign ||
            sanitizedRow.Campaign ||
            "",
        ),
        campaignId: String(
          sanitizedRow.campaignId ||
            sanitizedRow.CampaignId ||
            sanitizedRow["Campaign ID"] ||
            sanitizedRow.campaign_id ||
            "",
        ),
        days: Number(sanitizedRow.days || sanitizedRow.Days || 7), // Default to 7 if not provided
        impr: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conv: metrics.conv,
        value: metrics.value,
        ctr: metrics.CTR,
        cpc: metrics.clicks > 0 ? metrics.cost / metrics.clicks : 0,
        cpa: metrics.CPA,
        roas: metrics.ROAS,
        cvr: metrics.CvR,
      };
    }),
  SMART_BIDDING_CHANGES: (data): SmartBiddingChangesData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        changeDateTime: String(
          sanitized["Change Date Time"] ||
            sanitized.changeDateTime ||
            sanitized.change_date_time ||
            sanitized.ChangeDateTime ||
            "",
        ),
        changeType: String(
          sanitized["Change Type"] ||
            sanitized.changeType ||
            sanitized.change_event_type ||
            sanitized.ChangeEvent ||
            "",
        ),
        changedFields: String(
          sanitized["Changed Fields"] ||
            sanitized.changedFields ||
            sanitized.changed_fields ||
            sanitized.ChangedFields ||
            "",
        ),
        userEmail: String(
          sanitized["User Email"] ||
            sanitized.userEmail ||
            sanitized.user_email ||
            sanitized.UserEmail ||
            "",
        ),
        campaignName: String(
          sanitized["Campaign Name"] ||
            sanitized.campaignName ||
            sanitized.campaign ||
            sanitized.CampaignName ||
            "",
        ),
        campaignId: String(
          sanitized["Campaign ID"] ||
            sanitized.campaignId ||
            sanitized.campaign_id ||
            sanitized.CampaignId ||
            "",
        ),
      };
    }),

  ON_PERFORMANCE: (data: any[]): OnPerformanceData[] => {
    return data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const result = {
        Campaign: String(
          sanitized.Campaign ||
            sanitized.campaign ||
            sanitized.campaignName ||
            "",
        ),
        ImprYesterday: Number(sanitized.ImprYesterday || 0),
        ImprAvg13W: Number(sanitized.ImprAvg13W || 0),
        ImprDrop13W: Number(sanitized.ImprDrop13W || 0),
        ImprLevel13W: String(sanitized.ImprLevel13W || ""),
        ImprAvg4W: Number(sanitized.ImprAvg4W || 0),
        ImprDrop4W: Number(sanitized.ImprDrop4W || 0),
        ImprLevel4W: String(sanitized.ImprLevel4W || ""),
        CostYesterday: Number(sanitized.CostYesterday || 0),
        CostAvg13W: Number(sanitized.CostAvg13W || 0),
        CostDrop13W: Number(sanitized.CostDrop13W || 0),
        CostLevel13W: String(
          sanitized.CostLevel13W || sanitized.costLevel13W || "",
        ),
        CostAvg4W: Number(sanitized.CostAvg4W || 0),
        CostDrop4W: Number(sanitized.CostDrop4W || 0),
        CostLevel4W: String(
          sanitized.CostLevel4W || sanitized.costLevel4W || "",
        ),
        ConvYesterday: Number(sanitized.ConvYesterday || 0),
        ConvAvg13W: Number(sanitized.ConvAvg13W || 0),
        ConvDrop13W: Number(sanitized.ConvDrop13W || 0),
        ConvLevel13W: String(row.ConvLevel13W || ""),
        ConvAvg4W: Number(row.ConvAvg4W || 0),
        ConvDrop4W: Number(row.ConvDrop4W || 0),
        ConvLevel4W: String(row.ConvLevel4W || ""),
      };
      return result;
    });
  },
  ON_SETTINGS: (data: any[]): OnSettingsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaign: String(
          sanitized.campaign ||
            sanitized.Campaign ||
            sanitized.campaignName ||
            "",
        ),
        campaignId: String(
          sanitized.campaignId ||
            sanitized.CampaignId ||
            sanitized.campaign_id ||
            "",
        ),
        status: String(sanitized.status || sanitized.Status || ""),
        channel: String(sanitized.channel || sanitized.Channel || ""),
        targetContentNetwork: Boolean(
          sanitized.targetContentNetwork === true ||
            sanitized.targetContentNetwork === "true",
        ),
        networkType: String(
          sanitized.networkType || sanitized.NetworkType || "",
        ),
        geoTargetTypePos:
          sanitized.geoTargetTypePos ||
          sanitized.GeoTargetTypePos ||
          sanitized.geo_target_type_pos
            ? String(
                sanitized.geoTargetTypePos ||
                  sanitized.GeoTargetTypePos ||
                  sanitized.geo_target_type_pos,
              )
            : undefined,
        impr: Number(sanitized.impr || sanitized.Impressions || 0),
        clicks: Number(sanitized.clicks || sanitized.Clicks || 0),
        cost: Number(sanitized.cost || sanitized.Cost || 0),
        conv: Number(sanitized.conv || sanitized.Conversions || 0),
        value: Number(sanitized.value || sanitized.ConvValue || 0),
        tcpaTarget:
          sanitized.tcpaTarget || sanitized.TcpaTarget || sanitized.tcpa_target
            ? Number(
                sanitized.tcpaTarget ||
                  sanitized.TcpaTarget ||
                  sanitized.tcpa_target,
              )
            : undefined,
        troasTarget:
          sanitized.troasTarget ||
          sanitized.TroasTarget ||
          sanitized.troas_target
            ? Number(
                sanitized.troasTarget ||
                  sanitized.TroasTarget ||
                  sanitized.troas_target,
              )
            : undefined,
        lostBudget:
          sanitized.lostBudget || sanitized.LostBudget || sanitized.lost_budget
            ? Number(
                sanitized.lostBudget ||
                  sanitized.LostBudget ||
                  sanitized.lost_budget,
              )
            : undefined,
      };
    }),
  ON_DISAPPROVED: (data: any[]): OnDisapprovedData[] =>
    data.map((row) => ({
      campaign: String(row.campaign || ""),
      adGroupName: String(row.adGroupName || ""),
      adId: String(row.adId || ""),
      approvalStatus: String(row.approvalStatus || ""),
      policyTopics: String(row.policyTopics || "[]"),
    })),
  ON_EMPTY_AD_GROUPS: (data: any[]): OnEmptyAdGroupsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaign: String(
          sanitized.campaign ||
            sanitized.Campaign ||
            sanitized.campaignName ||
            "",
        ),
        campaignId: String(sanitized.campaignId || sanitized.CampaignId || ""),
        status: String(sanitized.status || sanitized.Status || ""),
        adGroupName: String(
          sanitized.adGroupName || sanitized.AdGroupName || "",
        ),
        adgroupid: String(
          sanitized.adgroupid ||
            sanitized.adGroupId ||
            sanitized.AdGroupId ||
            "",
        ),
        adgroupstatus: String(
          sanitized.adgroupstatus ||
            sanitized.adGroupStatus ||
            sanitized.AdGroupStatus ||
            "",
        ),
      };
    }),

  // Intent Mapper transformers - passthrough with sanitization
  IM_META: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_CAMPAIGNS: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_KEYWORDS: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_SEARCH_TERMS: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_PRODUCTS: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_ASSET_GROUPS: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_ADS: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_AUDIENCES: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_PMAX_CATEGORIES: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
  IM_PMAX_SEARCH_TERMS: (data: any[]): Record<string, any>[] =>
    data.map((row) => sanitizeStrings(row)),
};

export function isValidAccountCID(cid: string): boolean {
  return Boolean(
    cid &&
      typeof cid === "string" &&
      (/^\d+$/.test(cid) || /^\d{3}-\d{3}-\d{4}$/.test(cid)),
  );
}

interface AccountValidationResult {
  isValid: boolean;
  error?: string;
}

interface ValidateAccountInfo {
  accountCID?: string;
  account_cid?: string;
  cid?: string;
  CID?: string;
  accountName?: string;
  account_name?: string;
  name?: string;
  NAME?: string;
}

export function validateAccountData(
  accountInfo: ValidateAccountInfo,
): AccountValidationResult {
  if (!accountInfo) {
    return { isValid: false, error: "No account data received" };
  }

  // Support all legacy field names for backward compatibility
  const cid = String(
    accountInfo.accountCID ||
      accountInfo.account_cid ||
      accountInfo.cid ||
      accountInfo.CID ||
      "",
  );
  const name = String(
    accountInfo.accountName ||
      accountInfo.account_name ||
      accountInfo.name ||
      accountInfo.NAME ||
      "",
  );

  if (!cid) {
    return { isValid: false, error: "Missing account CID" };
  }

  if (!isValidAccountCID(cid)) {
    return { isValid: false, error: "Invalid account CID format" };
  }

  if (!name) {
    return { isValid: false, error: "Missing account name" };
  }

  return { isValid: true };
}

// Helper to safely get data for new tabs
function getSafeTabData(data: any[], tab: string): any[] {
  try {
    // Special case: if data has an 'error' field and mentions 'not found'
    if (data.length === 1 && data[0]?.error?.includes("not found")) {
      console.debug(`Tab ${tab} not found:`, data[0].error);
      return [];
    }

    // If no data or empty array, return empty array
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    // If transformer exists, try to use it
    if (transformers[tab as keyof typeof SHEET_TABS]) {
      try {
        return transformers[tab as keyof typeof SHEET_TABS](data);
      } catch (err) {
        console.error(`Transform failed for ${tab}:`, err);
        return [];
      }
    }

    // If no transformer, return raw data
    return data;
  } catch (err) {
    console.debug(`Failed to transform tab ${tab}:`, err);
    return [];
  }
}

export const SHEET_UNREADABLE_MESSAGE =
  "Nothing came back from your sheet. Check this account's Web App URL below.";

// Some tabs are legitimately absent (the script skips a few datasets), so
// errors on their own are normal. Only a fetch where NOTHING came back means
// we couldn't reach the sheet - a bad Web App URL, a deployment that isn't
// public, or a URL pointing at a sheet that isn't ours.
export function isSheetUnreadable(data?: StorageData | null): boolean {
  const meta = data?.fetchMeta;
  if (!meta) return false;
  return meta.loadedTabs === 0 && Object.keys(meta.errors).length > 0;
}

// True when the account tab specifically didn't come back, so anything we'd
// say about the script version is a guess based on a stale reading.
export function isAccountTabUnreadable(data?: StorageData | null): boolean {
  const meta = data?.fetchMeta;
  if (!meta) return false;
  return Boolean(meta.errors[SHEET_TABS.ACCOUNT]);
}

interface TabError {
  tabName: string;
  message: string;
}

interface FetchResult<T = unknown> {
  tabName: string;
  key: string;
  data: T[];
  success: boolean;
  error?: string;
}

export async function fetchAllData(
  accountCID: string,
  webAppUrl: string,
): Promise<StorageData> {
  console.log("fetchAllData: Starting for", {
    accountCID,
    webAppUrl,
    isSample: accountCID === SAMPLE_ACCOUNT_ID,
  });

  if (!accountCID) {
    throw new Error("Account CID is required");
  }

  // Handle sample data
  if (accountCID === SAMPLE_ACCOUNT_ID) {
    const sampleData = await loadSampleData();
    if (DEBUG) {
      console.log("Sample data loaded:", {
        placementsCount: sampleData[SHEET_TABS.PLACEMENTS]?.length,
        sample: sampleData[SHEET_TABS.PLACEMENTS]?.slice(0, 2),
      });
    }
    return sampleData;
  }

  // Start with empty storage data
  const storageData = createEmptyStorageData(accountCID);
  const errors: Record<string, string> = {};

  // Fetch all tabs in parallel
  const fetchPromises = Object.entries(SHEET_TABS).map(
    async ([key, tabName]): Promise<FetchResult<unknown>> => {
      try {
        // Add retry logic
        let attempts = 0;
        const maxAttempts = 3;

        while (attempts < maxAttempts) {
          try {
            const response = await fetch(`${webAppUrl}?tab=${tabName}`);

            if (!response.ok) {
              throw new Error(
                `HTTP ${response.status}: ${response.statusText}`,
              );
            }

            const data = await response.json();

            if (!Array.isArray(data)) {
              throw new Error(`Invalid data format - expected array`);
            }

            // Special handling for empty or error responses
            if (data.length === 1 && data[0]?.error) {
              throw new Error(data[0].error);
            }

            // Transform the data
            const transformedData =
              transformers[key as keyof typeof SHEET_TABS](data);

            return {
              tabName,
              key,
              data: transformedData,
              success: true,
            };
          } catch (err) {
            attempts++;
            if (attempts === maxAttempts) {
              throw err;
            }
            // Wait before retry (exponential backoff)
            await new Promise((resolve) =>
              setTimeout(resolve, attempts * 1000),
            );
          }
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Unknown error";
        errors[tabName] = errorMsg;

        return {
          tabName,
          key,
          data: [],
          success: false,
          error: errorMsg,
        };
      }
    },
  );

  try {
    const results = await Promise.allSettled(fetchPromises);
    let totalRecords = 0;
    let loadedTabs = 0;

    results.forEach((result) => {
      if (result.status === "fulfilled" && result.value) {
        const { key, data, success } = result.value;
        const tabName = SHEET_TABS[key as keyof typeof SHEET_TABS];
        if (tabName) {
          storageData[tabName] = data as any; // Necessary evil due to dynamic nature
          totalRecords += data.length;
          // A failed tab still resolves (with an empty array), so only count
          // the ones that actually came back.
          if (success) loadedTabs++;
        }
      }
    });

    storageData.fetchMeta = {
      totalRecords,
      loadedTabs,
      errors,
      timestamp: new Date().toISOString(),
    };

    storageData.records = {} as Record<string, Campaign>;

    console.log("fetchAllData: Complete", {
      accountCID,
      totalRecords,
      loadedTabs,
      errorCount: Object.keys(errors).length,
      timestamp: storageData.timestamp,
    });

    return storageData;
  } catch (err) {
    console.error("Failed to fetch data:", err);
    throw err;
  }
}
